



















































































import { Component, Vue, Watch } from 'vue-property-decorator';
import notificationService from '@/services/notificationService';
import { BillingType, MeasuringStatesClient, ListForFollowUpQuery, DataSourceLoadOptionsBase, PeriodType, SetMultipleQualityCheckedCommand } from '@/data/web-api-client';
import Card from '@/components/default/Card.vue';
import Datagrid from '@/components/default/Datagrid.vue';
import DataSource from 'devextreme/data/data_source';
import dxCustomStore from 'devextreme/data/custom_store';
import dxDataGrid from 'devextreme/ui/data_grid';
import employeesRepository from '@/modules/employees/employeesRepository';
import { selectableEmployees } from '@/modules/employees/employeeSelection';
import { loadFilterValue, saveFilterValue } from '@/helpers/viewFilterStorage';

// Storage keys for the view-level period/billing type filters, scoped to this view. Named after the
// grid's own dx-state-storing key ('measuringStatesFollowUpTable') for consistency, since that is
// the persistence pattern being mirrored here.
const BILLING_TYPE_STORAGE_KEY = 'measuringStatesFollowUpTable_billingType';
const PERIOD_TYPE_STORAGE_KEY = 'measuringStatesFollowUpTable_periodType';

@Component({
  components: {
    Card,
    Datagrid,
  },
})
export default class FollowUpOverview extends Vue {
  loading = false;

  billingType: TypeOrNull<BillingType> = loadFilterValue<TypeOrNull<BillingType>>(BILLING_TYPE_STORAGE_KEY, null);
  billingTypeEnum = BillingType;

  periodType: PeriodType = loadFilterValue<PeriodType>(PERIOD_TYPE_STORAGE_KEY, PeriodType.ExcludingCurrentMonth);
  periodTypeEnum = PeriodType;

  totalCount = 0;
  employees: any[] = [];
  executeBtnDisabled = true;

  private measuringStatesClient = new MeasuringStatesClient(undefined, this.$axios);

  async mounted() {
    await this.loadEmployees();
  }

  public openDetails(e: any) {
    if (e.row && e.column && e.column.type !== 'buttons' && e.column.type !== 'selection') {
      this.$router.push({
        path: `/measuringstates/${e.row.data.documentNumber.replace('/', '-').replace('/', '-')}`,
      });
    }
  }

  public async setSelectedMeasuringStatesQualityChecked() {
    if (!this.gridInstance.getSelectedRowsData().length) {
      return;
    }

    if (!confirm('Uitvoering van alle geselecteerde meetstaten gecontroleerd?')) {
      return;
    }

    this.loading = true;

    try {
      const measuringStates = this.gridInstance.getSelectedRowsData().map((ms) => ms.documentNumber);
      const command = new SetMultipleQualityCheckedCommand({
        documentNumbers: measuringStates,
        periodType: this.periodType,
      });

      await this.measuringStatesClient.setMultipleQualityChecked(command);
      await this.gridInstance.refresh();
      this.gridInstance.clearSelection();
    } catch (err) {
      notificationService.showAxiosError(err);
    } finally {
      this.loading = false;
    }
  }

  public onSelectionChanged(e: any) {
    this.executeBtnDisabled = !e?.selectedRowsData?.length;
  }

  async loadEmployees() {
    try {
      this.loading = true;
      this.employees = (await employeesRepository.get()).data;
      // OS-214: resolves (and caches) which employees are selectable as project leader / quality
      // guard, based on O365 group membership. Cached in the store, so this is a no-op after the
      // first call in the app session. Awaited before the grid renders: otherwise the lookup
      // columns are rebuilt once the groups arrive, which resets the restored filter values.
      await this.$store.dispatch('employees/fetchRoleGroups', { axios: this.$axios });
    } catch {
      notificationService.showErrorMessage('Er is iets misgelopen tijdens het inladen van de medewerkers');
    } finally {
      this.loading = false;
    }
  }

  get columns() {
    return [
      {
        dataField: 'projectNumber',
        caption: 'Projectnr.',
      },
      {
        dataField: 'projectName',
        caption: 'Projectnaam',
      },
      {
        dataField: 'customer',
        caption: 'Referentie (klant)',
      },
      {
        dataField: 'documentNumber',
        caption: 'Meetstaatnr.',
      },
      {
        dataField: 'name',
        caption: 'Titel',
      },
      {
        dataField: 'percentageExecuted',
        caption: '% uitgevoerd',
      },
      {
        dataField: 'projectLeader',
        caption: 'Projectleider',
        lookup: {
          dataSource: this.projectLeaderSources,
        },
      },
      {
        dataField: 'qualityGuard',
        caption: 'Quality Guard',
        lookup: {
          dataSource: this.qualityGuardSources,
        },
      },
      {
        dataField: 'subcontractor',
        caption: 'Onderaannemer',
      },
    ];
  }

  get gridInstance(): dxDataGrid {
    return (this.$refs.grid as any)?.$refs.measuringStatesFollowUpTable.instance as dxDataGrid;
  }

  get datasource() {
    const self = this;

    return new DataSource({
      store: new dxCustomStore({
        key: 'documentNumber',
        load: async (loadOptions) => {
          if (loadOptions.searchExpr && loadOptions.searchValue && loadOptions.searchOperation) {
            loadOptions.filter = [loadOptions.searchExpr, loadOptions.searchOperation, loadOptions.searchValue];
          }

          loadOptions.requireTotalCount = true;

          const result = await this.measuringStatesClient.listForFollowUp(
            new ListForFollowUpQuery({
              billingType: self.billingType ?? undefined,
              periodType: self.periodType,
              options: DataSourceLoadOptionsBase.fromJS(loadOptions),
            })
          );

          this.totalCount = result.totalCount!;

          return {
            data: result.data,
            totalCount: result.totalCount,
          };
        },
      }),
      paginate: true,
    });
  }

  // OS-214: these feed the grid's filter-row dropdowns, not an edit field, so a disabled /
  // non-selectable employee is simply left out - matches the ticket's own "Quality Guard-filter"
  // example. Rows that were historically assigned to such an employee keep showing their name
  // (the grid just displays the stored string), they just can't be picked as a new filter value.
  get projectLeaderSources() {
    return selectableEmployees(this.employees, {
      role: 'projectLeader',
      roleGroups: {
        projectLeaderIds: this.$store.getters['employees/projectLeaderIds'],
        qualityGuardIds: this.$store.getters['employees/qualityGuardIds'],
      },
    }).map((e) => e.displayName);
  }

  get qualityGuardSources() {
    return selectableEmployees(this.employees, {
      role: 'qualityGuard',
      roleGroups: {
        projectLeaderIds: this.$store.getters['employees/projectLeaderIds'],
        qualityGuardIds: this.$store.getters['employees/qualityGuardIds'],
      },
    }).map((e) => e.displayName);
  }

  @Watch('billingType')
  @Watch('periodType')
  async onPropsChanged(newValue: any, oldValue: any) {
    saveFilterValue(BILLING_TYPE_STORAGE_KEY, this.billingType);
    saveFilterValue(PERIOD_TYPE_STORAGE_KEY, this.periodType);
    await this.gridInstance.refresh();
  }
}
